import React, { useState } from 'react';
import axios from 'axios';
import { MapPin, Phone, Mail, Clock, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
    if (!formData.message.trim() || formData.message.length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      setSubmitSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
      
      setTimeout(() => setSubmitSuccess(false), 5000);
    } catch (error) {
      console.error('Error submitting contact:', error);
      alert('Failed to send message. Please try calling us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0D1B2A] via-[#1a2d42] to-[#0D1B2A]">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `repeating-linear-gradient(0deg, #C9A84C, #C9A84C 1px, transparent 1px, transparent 20px),
                           repeating-linear-gradient(90deg, #C9A84C, #C9A84C 1px, transparent 1px, transparent 20px)`
        }}></div>
      </div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-playfair font-bold text-[#C9A84C] mb-4">
            Contact Us
          </h2>
          <div className="w-24 h-1 bg-[#C9A84C] mx-auto mb-6"></div>
          <p className="text-gray-300 font-dmsans max-w-3xl mx-auto">
            Reach out to us for professional assistance. We have offices in Jaipur, India and Dubai, UAE.
          </p>
        </div>

        {/* Office Cards */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {/* India Office */}
          <div className="bg-[#FAF8F3] p-8 rounded-lg shadow-lg border-t-4 border-[#C9A84C]">
            <h3 className="text-2xl font-playfair font-bold text-[#0D1B2A] mb-6">
              India Head Office
            </h3>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <MapPin className="text-[#C9A84C] flex-shrink-0 mt-1" size={24} />
                <div className="font-dmsans text-gray-700">
                  <p className="font-semibold text-[#0D1B2A]">AGDG & Associates, Chartered Accountants</p>
                  <p>R-8A, 2nd Floor, Yudhishthir Marg</p>
                  <p>C-Scheme, Jaipur – 302006</p>
                  <p>Rajasthan, India</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Phone className="text-[#C9A84C] flex-shrink-0 mt-1" size={24} />
                <div className="font-dmsans text-gray-700">
                  <p>+91-9799494481</p>
                  <p>+91-9314922355</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Mail className="text-[#C9A84C] flex-shrink-0 mt-1" size={24} />
                <div className="font-dmsans text-gray-700">
                  <p>auditors@agdgassociates.com</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Clock className="text-[#C9A84C] flex-shrink-0 mt-1" size={24} />
                <div className="font-dmsans text-gray-700">
                  <p>Monday – Saturday</p>
                  <p>10:00 AM – 6:30 PM IST</p>
                </div>
              </div>
            </div>
          </div>

          {/* Dubai Office */}
          <div className="bg-[#FAF8F3] p-8 rounded-lg shadow-lg border-t-4 border-[#C9A84C]">
            <h3 className="text-2xl font-playfair font-bold text-[#0D1B2A] mb-6">
              International Branch (Dubai, UAE)
            </h3>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <MapPin className="text-[#C9A84C] flex-shrink-0 mt-1" size={24} />
                <div className="font-dmsans text-gray-700">
                  <p className="font-semibold text-white">AGDG & Associates</p>
                  <p>Villa 4, 352/27 Umm Sqim</p>
                  <p>Premise No. 352991356</p>
                  <p>PO Box 24589, Dubai</p>
                  <p>United Arab Emirates</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Mail className="text-[#C9A84C] flex-shrink-0 mt-1" size={24} />
                <div className="font-dmsans text-gray-700">
                  <p>auditors@agdgassociates.com</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Google Map */}
        <div className="mb-16">
          <h3 className="text-2xl font-playfair font-bold text-[#0D1B2A] mb-6 text-center">
            Locate Our Jaipur Office
          </h3>
          <div className="w-full h-96 rounded-lg overflow-hidden shadow-lg">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3557.8285468384766!2d75.79160631504413!3d26.917204883138873!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x396db41ec21c17a5%3A0x7df0a0b0a0b0a0b0!2sYudhishthir%20Marg%2C%20C%20Scheme%2C%20Ashok%20Nagar%2C%20Jaipur%2C%20Rajasthan%20302001!5e0!3m2!1sen!2sin!4v1234567890123!5m2!1sen!2sin"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="AGDG & Associates Jaipur Office Location"
            ></iframe>
          </div>
        </div>

        {/* Contact Form */}
        <div className="max-w-4xl mx-auto">
          <h3 className="text-3xl font-playfair font-bold text-[#C9A84C] mb-8 text-center">
            Send Us a Message
          </h3>

          {submitSuccess && (
            <div className="mb-8 p-6 bg-green-50 border-l-4 border-green-500 rounded-lg flex items-start gap-4">
              <CheckCircle className="text-green-500 flex-shrink-0 mt-1" size={24} />
              <div>
                <h4 className="font-playfair font-bold text-green-800 mb-1">Message Sent Successfully!</h4>
                <p className="text-green-700 font-dmsans text-sm">
                  Thank you for contacting us. We will respond to your message shortly.
                </p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="bg-[#1a2d42] bg-opacity-60 backdrop-blur-sm p-8 rounded-lg shadow-lg border border-[#C9A84C] border-opacity-20">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-dmsans font-semibold text-[#0D1B2A] mb-2">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A84C] ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Your name"
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-dmsans font-semibold text-[#0D1B2A] mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A84C] ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="your.email@example.com"
                />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-dmsans font-semibold text-[#0D1B2A] mb-2">
                  Phone <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A84C] ${
                    errors.phone ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="+91-XXXXXXXXXX"
                />
                {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
              </div>

              <div>
                <label className="block text-sm font-dmsans font-semibold text-[#0D1B2A] mb-2">
                  Subject <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A84C] ${
                    errors.subject ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Subject of your message"
                />
                {errors.subject && <p className="text-red-500 text-xs mt-1">{errors.subject}</p>}
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-dmsans font-semibold text-[#0D1B2A] mb-2">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="5"
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A84C] ${
                  errors.message ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Your message..."
              ></textarea>
              {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full py-4 rounded-lg font-dmsans font-bold text-lg transition-all duration-300 ${
                isSubmitting
                  ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                  : 'bg-[#C9A84C] text-[#0D1B2A] hover:bg-[#b8973d] transform hover:scale-105'
              }`}
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
